import React, {useEffect, useMemo, useState, useRef, ChangeEvent} from 'react';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import {
  Box,
  Center,
  Flex,
  Image,
  Text,
  useTheme,
  Button,
  VStack,
  HStack,
  Divider,
  Input,
} from '@chakra-ui/react';
import {TextG10, TextG11, TextG12, TextG14, TextG16, TextG18, TextG20} from '../typography';
import styled from 'styled-components';
import useAuth from '../../hooks/useAuth';
import NotificationsLauncher from '../NotificationsLauncher';
import Logo from '../../assets/logos/Word_logo3.png';
import GlobalSearch from '../GlobalSearch';
import useSuperFilters from '../../hooks/useSuperFilters';
import WhiteLogo from '../../assets/logos/Word_logo-white.png';
import useCurScrollPosition from '../../hooks/useCurScrollPosition';
import UserAvatar from '../UserAvatar';
import ProfilePhotoModal from '../../pages/Profile/Edit/ProfilePhotoModal';
import CommunitiesSelect, {ALL_COMMUNITY} from '../CommunitiesSelect';
import {useDispatch, useSelector} from 'react-redux';
import {filter, find} from 'lodash';
import {
  CommunityActions,
  saveCommunityToLocalStorage,
} from '../../state/Communities/CommunitiesSlice';
import useGlobalSearch from '../../hooks/useGlobalSearch';
import BackButton from '../BackButton';
import Icon from '../Icon';
import {CONTENT_TYPE} from '../../utils/GlobalSearch/enums';
import {getMembershipGradient} from '../../utils/membership';
import {BlueButton} from '../button';
import HandleEditModal from './HandleEditModal';
import {useAddressTypes, useGetUserAddresses} from '../../hooks/useUserAddress';
import {AddressTypes} from '../../types/address';

const HeaderText = styled(Text)`
  font-family: 'Aileron';
  font-weight: 800;
  font-size: 22px;
  color: #272929;

  @media (min-width: 321px) and (max-width: 375px) {
    font-size: 18px !important;
  }

  @media (min-width: 376px) and (max-width: 413px) {
    font-size: 20px !important;
  }

  @media (min-width: 413px) and (max-width: 429px) {
    font-size: 21px !important;
  }
`;

const StyledText = styled(Text)`
  font-family: 'Aileron';
  font-style: normal;
  font-weight: 900;
  font-size: 21px;
  line-height: 25px;
  letter-spacing: -0.01em;
  color: #e02f84;
`;

const StyledBox = styled(Box)<{$bannerHeight?: number}>`
  height: 128px;
  width: 100%;
  position: fixed;
  top: ${props => props.$bannerHeight || 0}px;
  left: 0;
  background-size: cover;
  background-color: white;
  z-index: 100;
`;

const PointsIndicator = styled(Box)`
  max-width: 88px;
  background: white;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  padding: 4px 12px;
  font-weight: 600;
  font-size: 12px;
  margin-top: -36px;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
  z-index: 0;
  color: black;
  padding-top: 36px;
  padding-bottom: 10px;
  background-color: #ffc3e0;
  text-align: center;
`;

const ActionButton = styled(Button)`
  border-radius: 30px !important;
  font-weight: 400 !important;
  height: 30px;
  padding: 4px 8px;
  white-space: nowrap;
  min-width: auto;
  font-family: 'Gordita';
  font-size: 14px !important;

  @media (min-width: 321px) and (max-width: 375px) {
    font-size: 11px !important;
  }

  @media (min-width: 376px) and (max-width: 413px) {
    font-size: 12px !important;
  }

  @media (min-width: 413px) and (max-width: 429px) {
    font-size: 13px !important;
  }
`;

const IconContainer = styled(Box)`
  position: relative;
  cursor: pointer;
`;

type TopBarProps = {
  hidePoints?: boolean;
  onClose?: () => void;
};

function TopBar({hidePoints}: TopBarProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const {user} = useAuth();
  const {pathname} = useLocation();
  const {superFiltersActive} = useSuperFilters();
  const [searchParams] = useSearchParams();
  const isProduction = process.env.NODE_ENV === 'production';
  const allowedDevUsers = user?.id !== 188 && user?.id !== 192;
  const selectedCommunityColor = useSelector((state: any) => state.community.community).color;
  const bannerHeight = useSelector((state: any) => state.globalBanner?.bannerHeight ?? 0);
  const {userAddresses, userAddressesLoading} = useGetUserAddresses({userId: user?.id});
  const {getIdForAddressType, addressTypesIsLoading} = useAddressTypes();
  const homeAddress = find(userAddresses, {type_id: getIdForAddressType(AddressTypes.Home)});
  const city = homeAddress?.city?.name;

  // State for profile photo modal
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<ChangeEvent<HTMLInputElement>>();
  const inputElement = useRef<HTMLInputElement | null>(null);

  // State for handle edit modal
  const [isHandleModalOpen, setIsHandleModalOpen] = useState(false);

  // Handler for direct file upload
  const handleUpload = (upload: ChangeEvent<HTMLInputElement>) => setUploadedImage(upload);

  const selectedCommunity = useSelector((state: any) => state.community.community);
  const dispatch = useDispatch();

  const {namedFilter, resetTypeSpecificFilters} = useGlobalSearch();

  const membershipColor = getMembershipGradient(user?.activeMembershipPlan || null, true);

  const userCommunities = filter(
    user?.communities,
    (community) => community.is_visible_in_selector
  );

  useEffect(() => {
    const isInCommunity = find(userCommunities, {id: selectedCommunity.id});
    if (user && !isInCommunity) {
      dispatch(CommunityActions.selectCommunity({community: ALL_COMMUNITY}));
      saveCommunityToLocalStorage(ALL_COMMUNITY);
    }
  }, [selectedCommunity, user]);

  const scrollPosition = useCurScrollPosition();

  const iconStyle: React.CSSProperties = React.useMemo(
    () => ({
      fontSize: '1.6rem',
      background: 'transparent',
      borderRadius: '100%',
    }),
    []
  );

  const namedFilterContent = (
    <Flex alignItems="center" bgColor={theme.colors.orange} p={3} mt={-4} mx={-3}>
      <BackButton
        type={'transparent'}
        pos={'relative'}
        onClick={() => {
          resetTypeSpecificFilters(CONTENT_TYPE.PLACE);
          if (searchParams.has('back')) {
            navigate(-1);
            return;
          } else if (searchParams.has('passType')) {
            navigate('/my-stuff?tab=Passes');
          } else if (pathname.indexOf('search')) {
            navigate('/home?openQuickLinks=true');
          } else {
            navigate(-1);
          }
        }}
      />
      <Box flex="1">
        <Center ml="-35px">
          {namedFilter?.icon && namedFilter?.color && (
            <Icon
              iconName={namedFilter.icon}
              style={{color: namedFilter.color, fontSize: '18px', paddingRight: '8px'}}
            />
          )}
          {namedFilter?.title && <TextG14 fontWeight={'bold'}>{namedFilter.title}</TextG14>}
        </Center>
      </Box>
    </Flex>
  );

  const topBarContent = useMemo(() => {
    return (
      <>
        {/* <Box
          zIndex={-1}
          pos="absolute"
          top="0"
          w="100vw"
          height="235px"
          background={getMembershipGradient(user?.activeMembershipPlan || null, false)}
        /> */}
        <Box
          id="topbar-tooltip-highlight"
          background={'white'}
          position="fixed"
          top={`${bannerHeight}px`}
          right={0}
          width="100vw"
          height="145px"
          zIndex={100}
          backgroundSize="cover">
          <Flex
            boxShadow={'0px 3px 10px rgba(0, 0, 0, 0.08)'}
            px="12px"
            pt={hidePoints ? '0' : '16px'}
            flexDirection="column"
            background="white"
            borderBottomRadius={'20px'}>
            {namedFilter
              ? namedFilterContent
              : !hidePoints && (
                  <>
                    <Flex w={'100%'} justifyContent="space-between" alignItems="flex-start">
                      {/* Left Section - Large Avatar with Points */}
                      <Flex direction="column" alignItems="center" position="relative">
                        <Box position="relative" zIndex={1}>
                          <UserAvatar
                            id="profile-tooltip"
                            user={user}
                            size="xl"
                            onClick={() => setShowPhotoModal(true)}
                          />
                          <Input
                            ref={inputElement}
                            type="file"
                            id="topbar-image-file"
                            accept="image/*;capture=camera"
                            onChange={handleUpload}
                            hidden
                          />
                        </Box>
                        {/* <PointsIndicator>
                          <TextG11 sx={{wordWrap: 'normal'}}>
                            {user?.pointsTotal.toLocaleString()} Points
                          </TextG11>
                        </PointsIndicator> */}
                      </Flex>

                      {/* Middle Section - Welcome Text and Buttons */}
                      <VStack spacing={1} align="flex-start" flex={1} px={3} gap="4px">
                        <HeaderText fontWeight="700" color={theme.colors.black}>
                          Hey {user?.name}! 👋
                        </HeaderText>
                        <Flex alignItems="center">
                          <TextG12
                            textDecoration="underline !important"
                            color={theme.colors.blue}
                            cursor="pointer"
                            position="relative"
                            onClick={() => setIsHandleModalOpen(true)}>
                            @{user?.handle}
                          </TextG12>
                          <Box mx={2} height="20px">
                            <Divider
                              orientation="vertical"
                              borderColor={theme.colors.darkGrey}
                              opacity={0.5}
                            />
                          </Box>
                          <TextG12
                            textDecoration="underline !important"
                            color={theme.colors.blue}
                            cursor="pointer"
                            onClick={() => navigate('/my-profile')}>
                            See Profile
                          </TextG12>
                        </Flex>
                        <TextG12 my="0px !important">
                          {user?.hasActiveMembership ? 'Insiders Member' : 'Free User'}
                          {city ? ` in ${city}` : ''}
                        </TextG12>
                        <Flex width="100%" mt={'0px !important'} gap={2}>
                          <TextG12 mt={'0px !important'} sx={{wordWrap: 'normal'}}>
                            {user?.pointsTotal.toLocaleString()} Points
                          </TextG12>

                          <TextG12
                            mt={'0px !important'}
                            onClick={() => navigate('/search/offer')}
                            color="blue"
                            textDecoration="underline !important">
                            Spend 'em!
                          </TextG12>

                          {/* <ActionButton
                            px={2.5}
                            h="auto"
                            colorScheme="white"
                            variant="outline"
                            onClick={() =>
                              navigate(`/membership/${user?.activeMembershipPlan.id}`)
                            }>
                            <Box mt={'0.25rem'}>Manage Membership</Box>
                          </ActionButton>
                          <ActionButton
                            px={2.5}
                            h="auto"
                            colorScheme="white"
                            variant="outline"
                            onClick={() => navigate('/create-meetup')}>
                            <Box mt={'0.25rem'}>Host a Meet-Up</Box>
                          </ActionButton> */}
                        </Flex>
                      </VStack>

                      {/* Right Section - Notifications and Settings */}
                      <Flex alignItems="center" justifyContent="flex-end" width="0px">
                        <IconContainer>
                          <Icon
                            onClick={() => navigate('/profile')}
                            iconName="fi-rr-settings"
                            style={iconStyle}
                          />
                        </IconContainer>
                        <NotificationsLauncher />
                      </Flex>
                    </Flex>
                  </>
                )}
            <GlobalSearch />
          </Flex>
        </Box>
      </>
    );
  }, [
    pathname,
    superFiltersActive,
    scrollPosition,
    selectedCommunityColor,
    namedFilter,
    user,
    iconStyle,
    bannerHeight,
  ]);

  return (
    <>
      {topBarContent}
      <ProfilePhotoModal
        show={showPhotoModal}
        setShow={setShowPhotoModal}
        uploadedImage={uploadedImage}
      />
      <HandleEditModal
        isOpen={isHandleModalOpen}
        onClose={() => setIsHandleModalOpen(false)}
        currentHandle={user?.handle || ''}
      />
    </>
  );
}
export default TopBar;
